import { GoogleMark } from "@/components/GoogleMark";
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { takeNext } from "@/lib/utils";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Hammer, Loader2, HardHat, Eye, EyeOff, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { openInstallPopup } from "@/lib/installStore";
import { BrandMark } from "@/components/BrandMark";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  useEffect(() => { document.title = "Sign in | Cross Jobs"; }, []);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [resetSent, setResetSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "worker" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        const res = await api.forgotPassword(form.email);
        setResetSent(true);
        toast.success(res?.message || "If that email exists, a reset link is on its way");
      } else if (mode === "login") {
        await login(form.email, form.password, remember);
        toast.success("Welcome to Cross Jobs");
        navigate(takeNext());
      } else {
        await register({ name: form.name, email: form.email, password: form.password, role: form.role });
        toast.success("Welcome to Cross Jobs");
        navigate("/");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#0F2236] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1768839725085-829e6ac7ac26?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2236] via-[#0F2236]/75 to-[#0F2236]/30" />
        <Link to="/home" className="relative z-10 flex items-center gap-3 w-fit" data-testid="login-home-link">
          <BrandMark size="lg" tone="light" />
        </Link>
        <div className="relative z-10">
          <h1 className="font-display text-4xl lg:text-5xl font-black leading-tight tracking-tight">
            Field crew,<br />coordinated.
          </h1>
          <p className="mt-4 text-white/70 text-lg max-w-md">
            Dispatch jobs, track site time, capture proof-of-work and run your crew from any phone or desktop.
          </p>
          <div className="mt-8 flex gap-6 text-sm text-white/60">
            <span className="flex items-center gap-2"><HardHat className="w-4 h-4 text-[#EA580C]" /> Live job tracking</span>
            <span className="flex items-center gap-2"><Hammer className="w-4 h-4 text-[#EA580C]" /> Timesheets & rosters</span>
          </div>
        </div>
        <p className="relative z-10 text-xs text-white/40">© 2026 Cross Jobs</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/home" className="lg:hidden flex items-center gap-2 mb-8 w-fit" data-testid="login-home-link-mobile">
            <BrandMark />
          </Link>
          <h2 className="font-display text-3xl font-bold tracking-tight">
            {mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Reset password"}
          </h2>
          <p className="text-muted-foreground mt-1">
            {mode === "login" ? "Access your crew dashboard." : mode === "register" ? "Join your crew in seconds." : "We'll email you a link to set a new password."}
          </p>
          {!(window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) && (
            <button type="button" data-testid="login-install-btn" onClick={openInstallPopup}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors">
              <Download className="w-4 h-4" /> Install app on this device
            </button>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode !== "forgot" && (<>
            <button
              type="button"
              data-testid="google-signin-btn"
              onClick={googleLogin}
              className="w-full rounded-full border border-border bg-card hover:bg-secondary transition-colors h-11 flex items-center justify-center gap-3 font-medium"
            >
              <GoogleMark />
              Continue with Google
            </button>
            <div className="flex items-center gap-3 py-1">
              <span className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">or use email</span>
              <span className="h-px flex-1 bg-border" />
            </div>
            </>)}
            {mode === "register" && (
              <div className="space-y-2">
                <Label>Full name</Label>
                <Input data-testid="auth-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" />
              </div>
            )}
            <div className="space-y-2">
              <Label>{mode === "login" ? "Email or username" : "Email"}</Label>
              <Input data-testid="auth-email" type={mode === "login" ? "text" : "email"} autoCapitalize="none" autoCorrect="off" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com.au" />
            </div>
            {mode !== "forgot" && (
            <div className="space-y-2">
              <Label>Password</Label>
              <div className="relative">
                <Input data-testid="auth-password" type={showPw ? "text" : "password"} required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" className="pr-11" />
                <button type="button" data-testid="toggle-password" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            )}
            {mode === "login" && (
              <button type="button" data-testid="forgot-password-btn" onClick={() => { setMode("forgot"); setResetSent(false); }} className="text-sm font-medium text-primary hover:underline">
                Forgot password?
              </button>
            )}
            {mode === "login" && (
              <label data-testid="remember-me" className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-me-checkbox" />
                Keep me signed in on this device
              </label>
            )}
            {mode === "register" && (
              <div className="space-y-2">
                <Label>I am a</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[["worker", "Field Worker"], ["coordinator", "Coordinator"]].map(([val, label]) => (
                    <button
                      type="button"
                      key={val}
                      data-testid={`role-${val}`}
                      onClick={() => set("role", val)}
                      className={`rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                        form.role === val ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {mode === "forgot" && resetSent && (
              <p data-testid="reset-sent-note" className="text-sm text-emerald-600 bg-emerald-500/10 rounded-lg px-3 py-2">
                Check your inbox — if an account exists for that email, a reset link is on its way. It expires in 1 hour.
              </p>
            )}

            <Button type="submit" data-testid="auth-submit" disabled={loading} className="w-full rounded-full bg-primary hover:bg-primary/90 text-white h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            {mode === "login" ? "New to Cross Jobs?" : mode === "register" ? "Already have an account?" : "Remembered your password?"}{" "}
            <button
              data-testid="auth-toggle"
              className="font-semibold text-primary hover:underline"
              onClick={() => { setMode(mode === "login" ? "register" : mode === "register" ? "login" : "login"); setResetSent(false); }}
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
